import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useRealtime } from '@/hooks/useRealtime';
import { useAuth } from '@/hooks/useAuth';
import AuthGuard from '@/components/feature/AuthGuard';
import AdminLayout from '@/pages/dashboard/admin/components/AdminLayout';

interface SponsorCategory {
  id: string;
  name: string;
  is_active: boolean;
}

interface Sponsor {
  id: string;
  category_id: string | null;
  name: string;
  website_url: string | null;
  logo_url: string | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  category_name?: string;
}

export default function SponsorsPage() {
  const { profile } = useAuth();
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [categories, setCategories] = useState<SponsorCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', category_id: '', website_url: '', logo_url: '', display_order: 0, is_active: true });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);

  const loadSponsors = useCallback(async () => {
    try {
      setLoading(true);
      const [spRes, catRes] = await Promise.all([
        supabase.from('sponsors').select('*').order('display_order', { ascending: true }),
        supabase.from('sponsor_categories').select('id, name, is_active').eq('is_active', true).order('display_order'),
      ]);
      if (spRes.error) throw spRes.error;
      if (catRes.error) throw catRes.error;

      const catMap: Record<string, string> = {};
      (catRes.data || []).forEach((c: SponsorCategory) => { catMap[c.id] = c.name; });

      setSponsors((spRes.data || []).map((s) => ({ ...s, category_name: s.category_id ? (catMap[s.category_id] || 'Uncategorized') : 'Uncategorized' })));
      setCategories(catRes.data || []);
      setError('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load sponsors');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSponsors(); }, [loadSponsors]);
  useRealtime<Sponsor>({ table: 'sponsors', onAny: () => loadSponsors() });
  useRealtime<SponsorCategory>({ table: 'sponsor_categories', onAny: () => loadSponsors() });

  const handleCreateCategory = async () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) return;
    setCreatingCategory(true);
    try {
      const { data, error: err } = await supabase
        .from('sponsor_categories')
        .insert({
          name: trimmed,
          display_order: categories.length,
          is_active: true,
          created_by: profile?.id,
        })
        .select('id, name, is_active')
        .single();
      if (err) throw err;
      if (data) {
        setCategories((prev) => [...prev, data]);
        setForm((prev) => ({ ...prev, category_id: data.id }));
        await supabase.from('audit_logs').insert({
          user_id: profile?.id, user_name: profile?.full_name || 'Admin', role: profile?.role,
          action: 'Sponsor Category Created (quick)', module: 'Sponsors',
        });
      }
      setNewCategoryName('');
      setShowNewCategoryInput(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create category');
    } finally { setCreatingCategory(false); }
  };

  const toggleActive = async (id: string, current: boolean) => {
    try {
      const { error: err } = await supabase.from('sponsors').update({ is_active: !current }).eq('id', id);
      if (err) throw err;
      await supabase.from('audit_logs').insert({
        user_id: profile?.id, user_name: profile?.full_name || 'Admin', role: profile?.role,
        action: `Sponsor ${!current ? 'Activated' : 'Deactivated'}`, module: 'Sponsors',
      });
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Failed to update'); }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error: err } = await supabase.from('sponsors').delete().eq('id', id);
      if (err) throw err;
      await supabase.from('audit_logs').insert({
        user_id: profile?.id, user_name: profile?.full_name || 'Admin', role: profile?.role,
        action: 'Sponsor Deleted', module: 'Sponsors',
      });
      setDeleteConfirm(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
      setDeleteConfirm(null);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Sponsor name is required');
      return;
    }
    if (!form.category_id) {
      setError('Please select a category');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editId) {
        const { error: err } = await supabase.from('sponsors').update({
          name: form.name.trim(), category_id: form.category_id, website_url: form.website_url.trim() || null,
          logo_url: form.logo_url.trim() || null, display_order: form.display_order, is_active: form.is_active,
        }).eq('id', editId);
        if (err) throw err;
        await supabase.from('audit_logs').insert({
          user_id: profile?.id, user_name: profile?.full_name || 'Admin', role: profile?.role,
          action: 'Sponsor Updated', module: 'Sponsors',
        });
      } else {
        const { error: err } = await supabase.from('sponsors').insert({
          name: form.name.trim(), category_id: form.category_id, website_url: form.website_url.trim() || null,
          logo_url: form.logo_url.trim() || null, display_order: form.display_order, is_active: form.is_active,
        });
        if (err) throw err;
        await supabase.from('audit_logs').insert({
          user_id: profile?.id, user_name: profile?.full_name || 'Admin', role: profile?.role,
          action: 'Sponsor Created', module: 'Sponsors',
        });
      }
      setShowModal(false); setEditId(null);
      setForm({ name: '', category_id: '', website_url: '', logo_url: '', display_order: 0, is_active: true });
      setError('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save sponsor');
    } finally { setSaving(false); }
  };

  const openEdit = (s: Sponsor) => {
    setEditId(s.id);
    setForm({ name: s.name, category_id: s.category_id || '', website_url: s.website_url || '', logo_url: s.logo_url || '', display_order: s.display_order || 0, is_active: s.is_active });
    setShowModal(true);
  };

  return (
    <AuthGuard requiredRole="admin">
      <AdminLayout title="Sponsors" breadcrumbs={[{ label: 'Dashboard', href: '/dashboard/admin' }, { label: 'Sponsors' }]}>
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm flex items-center justify-between">
            <div className="flex items-start gap-2">
              <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
              <span>{error}</span>
            </div>
            <button onClick={() => setError('')} className="cursor-pointer flex-shrink-0 ml-2"><i className="ri-close-line"></i></button>
          </div>
        )}

        <div className="flex justify-end mb-5">
          <button onClick={() => { setEditId(null); setForm({ name: '', category_id: '', website_url: '', logo_url: '', display_order: sponsors.length, is_active: true }); setShowModal(true); setError(''); }} className="px-4 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
            <i className="ri-add-line"></i> Add Sponsor
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div></div>
        ) : sponsors.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-building-line text-2xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-400 text-sm">No sponsors yet</p>
          </div>
        ) : (
          <div className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200/70 bg-background-100/50">
                    {['Sponsor', 'Category', 'Website', 'Active', 'Order', 'Created', 'Actions'].map((l) => (
                      <th key={l} className="px-4 py-3 text-left text-xs font-semibold text-foreground-500 whitespace-nowrap">{l}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sponsors.map((s) => (
                    <tr key={s.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {s.logo_url ? (
                            <img src={s.logo_url} alt={s.name} className="w-8 h-8 rounded object-contain bg-background-100" />
                          ) : (
                            <div className="w-8 h-8 rounded bg-background-100 flex items-center justify-center">
                              <i className="ri-building-line text-foreground-400 text-sm"></i>
                            </div>
                          )}
                          <span className="font-medium text-foreground-900">{s.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-foreground-600 text-xs">{s.category_name}</td>
                      <td className="px-4 py-3">
                        {s.website_url ? (
                          <a href={s.website_url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 text-xs whitespace-nowrap cursor-pointer truncate max-w-[120px] block">{(() => { try { return new URL(s.website_url).hostname; } catch { return s.website_url; } })()}</a>
                        ) : <span className="text-foreground-400 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button onClick={() => toggleActive(s.id, s.is_active)} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${s.is_active ? 'bg-accent-500' : 'bg-background-300'}`}>
                          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${s.is_active ? 'translate-x-5' : 'translate-x-0.5'}`}></div>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-foreground-600 text-xs">{s.display_order}</td>
                      <td className="px-4 py-3 text-foreground-500 text-xs whitespace-nowrap">{new Date(s.created_at).toLocaleDateString('en-IN')}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEdit(s)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-accent-50 text-foreground-400 hover:text-accent-600 transition-colors cursor-pointer" title="Edit">
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                          <button onClick={() => setDeleteConfirm(s.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-600 transition-colors cursor-pointer" title="Delete">
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Delete confirmation modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setDeleteConfirm(null)}>
            <div className="bg-background-50 rounded-2xl shadow-xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
              <div className="w-10 h-10 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-3">
                <i className="ri-error-warning-line text-lg text-red-600"></i>
              </div>
              <h3 className="font-heading text-base font-bold text-foreground-950 text-center mb-1">Delete Sponsor?</h3>
              <p className="text-sm text-foreground-500 text-center mb-5">This action cannot be undone.</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteConfirm(null)} className="flex-1 px-4 py-2 rounded-lg border border-background-200 text-foreground-600 text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancel</button>
                <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap">Delete</button>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowModal(false)}>
            <div className="bg-background-50 rounded-2xl shadow-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
              <div className="border-b border-background-200/70 p-5 flex items-center justify-between">
                <h3 className="font-heading text-lg font-bold text-foreground-950">{editId ? 'Edit' : 'Add'} Sponsor</h3>
                <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer">
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div><label className="block text-xs font-medium text-foreground-600 mb-1">Sponsor Name <span className="text-red-500">*</span></label>
                  <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 outline-none" placeholder="e.g. Apollo Hospitals" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Category <span className="text-red-500">*</span></label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-700 text-sm focus:border-primary-400 outline-none cursor-pointer appearance-none">
                        <option value="">Select category...</option>
                        {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
                    </div>
                    <button
                      onClick={() => { setShowNewCategoryInput(!showNewCategoryInput); setNewCategoryName(''); }}
                      className="w-10 h-10 flex items-center justify-center rounded-lg border border-background-200 hover:bg-background-100 text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer flex-shrink-0"
                      title="Create new category"
                    >
                      <i className="ri-add-line text-lg"></i>
                    </button>
                  </div>
                  {showNewCategoryInput && (
                    <div className="mt-2 p-3 rounded-lg border border-background-200 bg-background-100/50 flex items-center gap-2">
                      <input
                        type="text"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleCreateCategory(); }}
                        placeholder="New category name..."
                        className="flex-1 px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 outline-none"
                        autoFocus
                      />
                      <button
                        onClick={handleCreateCategory}
                        disabled={creatingCategory || !newCategoryName.trim()}
                        className="px-4 py-2 rounded-lg bg-accent-500 hover:bg-accent-600 disabled:bg-accent-300 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                      >
                        {creatingCategory ? (
                          <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin"></div>
                        ) : null}
                        {creatingCategory ? 'Creating...' : 'Create'}
                      </button>
                      <button
                        onClick={() => { setShowNewCategoryInput(false); setNewCategoryName(''); }}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer flex-shrink-0"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  )}
                  {categories.length === 0 && !showNewCategoryInput && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                      <i className="ri-information-line text-sm"></i>
                      No categories yet — click the <span className="font-semibold">+</span> button to create one
                    </p>
                  )}
                </div>
                <div><label className="block text-xs font-medium text-foreground-600 mb-1">Website URL</label>
                  <input type="url" value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 outline-none" placeholder="https://..." />
                </div>
                <div><label className="block text-xs font-medium text-foreground-600 mb-1">Logo Image URL</label>
                  <input type="url" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 outline-none" placeholder="https://..." />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-xs font-medium text-foreground-600 mb-1">Display Order</label>
                    <input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 outline-none" min="0" />
                  </div>
                  <div className="flex items-center gap-3 pt-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 rounded accent-primary-500" />
                      <span className="text-sm text-foreground-700">Active</span>
                    </label>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border border-background-200 text-foreground-600 text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancel</button>
                  <button onClick={handleSave} disabled={saving} className="px-5 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 disabled:cursor-not-allowed text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                    {saving && <div className="w-4 h-4 rounded-full border-2 border-background-50 border-t-transparent animate-spin"></div>}
                    {saving ? 'Saving...' : editId ? 'Save Changes' : 'Add Sponsor'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </AdminLayout>
    </AuthGuard>
  );
}